import * as THREE from 'three';

export class Player {
  private mesh!: THREE.Group;
  private body!: THREE.Mesh;
  private health = 100;
  private velocity = new THREE.Vector3();
  private isJumping = false;
  private isAttacking = false;
  private attackCooldown = 0;
  private invincible = 0;
  
  private readonly SPEED = 15;
  private readonly JUMP_FORCE = 12;
  private readonly GRAVITY = 30;
  
  constructor(scene: THREE.Scene) {
    this.createMesh(scene);
  }
  
  private createMesh(scene: THREE.Scene): void {
    this.mesh = new THREE.Group();
    
    const bodyGeo = new THREE.CapsuleGeometry(0.5, 1.5, 8, 16);
    const bodyMat = new THREE.MeshStandardMaterial({
      color: 0x00ff00,
      metalness: 0.3,
      roughness: 0.6
    });
    this.body = new THREE.Mesh(bodyGeo, bodyMat);
    this.body.position.y = 1.5;
    this.body.castShadow = true;
    this.mesh.add(this.body);
    
    const eyeGeo = new THREE.SphereGeometry(0.15, 8, 8);
    const eyeMat = new THREE.MeshBasicMaterial({ color: 0xff0000 });
    const leftEye = new THREE.Mesh(eyeGeo, eyeMat);
    leftEye.position.set(-0.2, 2.3, 0.4);
    this.mesh.add(leftEye);
    
    const rightEye = new THREE.Mesh(eyeGeo, eyeMat);
    rightEye.position.set(0.2, 2.3, 0.4);
    this.mesh.add(rightEye);
    
    const weaponGeo = new THREE.BoxGeometry(0.1, 0.1, 1.5);
    const weaponMat = new THREE.MeshStandardMaterial({
      color: 0x444444,
      metalness: 0.9,
      roughness: 0.1
    });
    const weapon = new THREE.Mesh(weaponGeo, weaponMat);
    weapon.position.set(0.8, 1.5, 0.8);
    this.mesh.add(weapon);
    
    scene.add(this.mesh);
  }
  
  setPosition(x: number, y: number, z: number): void {
    this.mesh.position.set(x, y, z);
  }
  
  getPosition(): THREE.Vector3 {
    return this.mesh.position.clone();
  }
  
  getHealth(): number {
    return this.health;
  }
  
  move(direction: THREE.Vector3, delta: number): void {
    if (direction.length() > 0) {
      direction.normalize();
      direction.applyAxisAngle(new THREE.Vector3(0, 1, 0), 0);
      
      const speed = this.isJumping ? this.SPEED * 0.5 : this.SPEED;
      this.velocity.x = direction.x * speed;
      this.velocity.z = direction.z * speed;
      
      this.mesh.rotation.y = Math.atan2(direction.x, direction.z);
    } else {
      this.velocity.x *= 0.9;
      this.velocity.z *= 0.9;
    }
    
    this.mesh.position.x += this.velocity.x * delta;
    this.mesh.position.z += this.velocity.z * delta;
    
    this.mesh.position.x = Math.max(-90, Math.min(90, this.mesh.position.x));
    this.mesh.position.z = Math.max(-90, Math.min(90, this.mesh.position.z));
  }
  
  jump(pressed: boolean): void {
    if (pressed && !this.isJumping) {
      this.velocity.y = this.JUMP_FORCE;
      this.isJumping = true;
    }
    
    if (this.isJumping) {
      this.velocity.y -= this.GRAVITY * (this.mesh.position.y > 0 ? 1 : -1);
      this.mesh.position.y += this.velocity.y * 0.016;
      
      if (this.mesh.position.y <= 0) {
        this.mesh.position.y = 0;
        this.velocity.y = 0;
        this.isJumping = false;
      }
    }
  }
  
  crouch(active: boolean): void {
    const targetY = active ? 0.5 : 1.5;
    const targetScale = active ? 0.7 : 1;
    
    this.body.position.y += (targetY - this.body.position.y) * 0.2;
    this.mesh.scale.y += (targetScale - this.mesh.scale.y) * 0.2;
  }
  
  attack(pressed: boolean): void {
    if (pressed && this.attackCooldown <= 0) {
      this.isAttacking = true;
      this.attackCooldown = 0.3;
      
      const weapon = this.mesh.children[3];
      if (weapon) {
        weapon.rotation.x = -Math.PI / 2;
        setTimeout(() => {
          weapon.rotation.x = 0;
          this.isAttacking = false;
        }, 150);
      }
    }
    
    this.attackCooldown -= 0.016;
  }
  
  tryAttack(): boolean {
    if (this.isAttacking && this.invincible <= 0) {
      this.invincible = 0.2;
      return true;
    }
    this.invincible -= 0.016;
    return false;
  }
  
  takeDamage(amount: number): void {
    if (this.invincible > 0) return;
    
    this.health = Math.max(0, this.health - amount);
    this.invincible = 0.5;
    
    const mat = this.body.material as THREE.MeshStandardMaterial;
    mat.emissive.set(0xff0000);
    mat.emissiveIntensity = 1;
    setTimeout(() => {
      mat.emissiveIntensity = 0;
    }, 100);
  }
}